import { Middleware } from '@reduxjs/toolkit';
import { fnDelayedThrottle, fnReload } from '../../services/shared';
import { processQueueThunk } from '../eventRegistration';
// actions moved to atoll/redux/actions/
import { ADD_MESSAGE, ADD_QUEUED_EVENT_LISTENER, ADD_TO_PREFSQUEUE, SET_SESSION, SET_VERSION } from './actions/types';
import { processPreferencesQueue } from './prefsSlice';
import { logout, setLoginOpen, updateUser } from './authSlice';
import { fnUserLoggedOut } from '../atollAuth';
import { callbackFunctions, triggerCallbacks } from '@rhino/shared/services/redux/shuteCallbackRegistry';
export { callbackFunctions, triggerCallbacks };



const ADMIN_FORCE_LOGOUT_ACTIONS = new Set([
    'atoll.admin.user.delete',
    'atoll.admin.token.revoke',
    'atoll.admin.token.revoke_all',
    'atoll.admin.service_token.revoke',
]);

const ADMIN_REFRESH_USER_ACTIONS = new Set([
    'atoll.admin.user.update',
    'atoll.admin.user.password_reset',
    'atoll.admin.role.update',
    'atoll.admin.role.delete',
]);

/**
 * React to admin events that affect the currently logged-in user.
 * Destructive events (delete, revoke) force logout.
 * Non-destructive events (update, role change) trigger a user data refresh.
 */
function handleAdminEvent(eventName: string, data: any, store: any): void {
    const state = store.getState();
    const loggedInUser = state.auth?.user;
    if (!loggedInUser) return;

    const eventData = data?.data ?? {};
    const targetUserID = eventData.userid;

    // For user/token events: only react if this user is the target
    if (targetUserID !== undefined && targetUserID !== loggedInUser.userid) return;

    // For role events: only react if this user has roles for the affected service
    if (targetUserID === undefined && eventData.service) {
        const userServices = loggedInUser.userattributes?.services;
        if (!userServices || !userServices[eventData.service]) return;
    }

    if (ADMIN_FORCE_LOGOUT_ACTIONS.has(eventName)) {
        console.warn(`[ATOLL] Admin event ${eventName} affects current user — forcing logout`);
        fnUserLoggedOut();
        store.dispatch(logout());
        store.dispatch(setLoginOpen(true));
    } else if (ADMIN_REFRESH_USER_ACTIONS.has(eventName)) {
        console.info(`[ATOLL] Admin event ${eventName} affects current user — refreshing user data`);
        store.dispatch(updateUser({ keyaddress: 'refresh', keyvalue: 0 }));
    }
}

export const combinedMiddleware: Middleware = store => {
    const throttledProcessQueueThunk = fnDelayedThrottle(() => {
        store.dispatch(processQueueThunk() as any);
    }, 500);

    return next => (action: { type: string; payload?: any }) => {
        let result;

        switch (action.type) {
            case SET_VERSION:
            case SET_SESSION:
                const prevState = store.getState(); // Get the previous state before the action is processed
                result = next(action);
                const nextState = store.getState(); // Get the next state after the action is processed
                // console.log(prevState.shute.version , nextState.shute.version)
                // Check if the version changed from a truthy value to another truthy value
                if (action.type===SET_VERSION && prevState.shute.version !== nextState.shute.version && prevState.shute.version && nextState.shute.version) {
                    try {console.log(`'${prevState.shute.version}' != '${nextState.shute.version}'`);} catch {console.log('some error')}
                    // let sGo = prompt("Version has changed, about to initiate reload.  Do you want to allow it?", "Y");
                    // console.log(sGo)
                    // if (sGo=="Y") {
                    fnReload();
                    // }
          
                } else if (action.type===SET_SESSION && prevState.shute.session !== nextState.shute.session && prevState.shute.session && nextState.shute.session) {
                    try {console.log(`'${prevState.shute.session}' != '${nextState.shute.session}'`);} catch {console.log('some error')}          
                }
                break;
                
            case ADD_MESSAGE:
                try {
                    const state = store.getState(); // Get the current state
                    // console.log(`[SHUTE-MSG] event=${action.payload.event}`)
                    if (action.payload.event) {
                        triggerCallbacks(action.payload.event, action.payload, state);
                        if (action.payload.event.startsWith('atoll.admin.')) {
                            handleAdminEvent(action.payload.event, action.payload, store);
                        }
                    }
                } catch (error) {
                    console.error(error)
                }
                result = next(action);
                break;
            
            case ADD_QUEUED_EVENT_LISTENER:
                result = next(action);
                try {
                    // console.log('ADD_QUEUED_EVENT_LISTENER throttle trigger')
                    throttledProcessQueueThunk();                
                } catch (error) {
                    console.error(error);
                }
                break;
            case ADD_TO_PREFSQUEUE:
                result = next(action);
                processPreferencesQueue(store.dispatch, store.getState);
                break;
            default:
                // console.log(action.type)
                result = next(action);
                break;
        }

        return result;
    };
};
